import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ZoomIn } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_QR_URL,
  GOOGLE_PLAY_QR_URL,
} from './appStoreUrls';

const QR_ZOOM_SIZE = 280;

const QRItem = ({
  label,
  url,
  size,
  prominent = false,
  clickable = true,
  onEnlarge,
}) => (
  <div className="flex flex-col items-center gap-3">
    {clickable ? (
      <button
        type="button"
        onClick={onEnlarge}
        className={`group relative rounded-2xl bg-white transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          prominent
            ? 'p-4 shadow-lg ring-1 ring-blue-100'
            : 'p-2 shadow-sm ring-1 ring-gray-200/80'
        }`}
        aria-label={`View larger ${label} QR code`}
      >
        <QRCodeSVG value={url} size={size} level="M" includeMargin={false} />
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-black/0 transition-colors duration-300 group-hover:bg-black/5">
          <span className="flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-medium text-gray-700 opacity-0 shadow-sm transition-opacity duration-300 group-hover:opacity-100">
            <ZoomIn className="h-3.5 w-3.5" />
            Enlarge
          </span>
        </span>
      </button>
    ) : (
      <div
        className={
          prominent
            ? 'rounded-2xl bg-white p-4 shadow-lg ring-1 ring-blue-100'
            : 'rounded-lg bg-white p-2 shadow-sm'
        }
      >
        <QRCodeSVG value={url} size={size} level="M" includeMargin={false} />
      </div>
    )}
    <span
      className={
        prominent
          ? 'text-sm font-semibold text-gray-700'
          : 'text-xs font-medium text-gray-400'
      }
    >
      {label}
    </span>
  </div>
);

const AppStoreQRCodes = ({
  className = '',
  size = 88,
  layout = 'row',
  useUniversalUrls = true,
  prominent = false,
  clickable = true,
}) => {
  const [expanded, setExpanded] = useState(null);

  const appleUrl = useUniversalUrls ? APP_STORE_QR_URL : APP_STORE_URL;
  const googleUrl = useUniversalUrls ? GOOGLE_PLAY_QR_URL : GOOGLE_PLAY_URL;

  const items = [
    { id: 'apple', label: 'App Store', url: appleUrl },
    { id: 'google', label: 'Google Play', url: googleUrl },
  ];

  const expandedItem = items.find((item) => item.id === expanded);

  return (
    <>
      <div
        className={`flex gap-4 ${layout === 'row' ? 'flex-row items-start' : 'flex-col items-center'} ${className}`}
      >
        {items.map((item) => (
          <QRItem
            key={item.id}
            label={item.label}
            url={item.url}
            size={size}
            prominent={prominent}
            clickable={clickable}
            onEnlarge={() => setExpanded(item.id)}
          />
        ))}
      </div>

      <Dialog open={!!expandedItem} onOpenChange={(open) => !open && setExpanded(null)}>
        <DialogContent className="sm:max-w-md text-center">
          {expandedItem && (
            <>
              <DialogHeader className="text-center sm:text-center">
                <DialogTitle className="text-xl">{expandedItem.label}</DialogTitle>
                <DialogDescription>
                  Scan this QR code with your phone camera to download the Rink app.
                </DialogDescription>
              </DialogHeader>

              <div className="flex justify-center py-4">
                <div className="rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-200">
                  <QRCodeSVG
                    value={expandedItem.url}
                    size={QR_ZOOM_SIZE}
                    level="H"
                    includeMargin
                  />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppStoreQRCodes;
